import autobind from 'autobind-decorator';
import * as React from 'react';
import {withTranslation, WithTranslation} from 'react-i18next';
import {Button, Card, Checkbox, Col, Divider, Icon, Input, InputNumber, Modal, Popconfirm, Row, Switch, Tooltip} from 'antd';
import {connect} from 'redux-scaffolding-ts';
import {Link, RouteComponentProps, withRouter} from 'react-router-dom';
import {EmployeesStore, EmployeeSummary} from 'src/stores/employees-store';
import {ItemState, Query} from 'src/core/stores/dataStore';
import {TableModel, TableView} from 'src/core/ui/collections/table';
import {formatMessage} from 'src/core/services/http.service';
import {CommandResult} from 'src/core/stores/types';
import NewEmployeeView from './employee-new';
import EmployeeEditor from 'src/components/employee-editor';
import {IdentityProps, withIdentity} from 'src/core/services/authentication';
import EditableTooltip from '../../../../components/editable-tooltip/tooltip';
import Tour from 'reactour';
import steps from 'src/components/virtual-tour/virtual-tour-configs';
import {notify} from 'src/core/ui/floatingNotification';
import {CacheProps, withCache} from '../../../../core/services/cache.service';
import EmployeeLinkerView from './employee-linker'
import {clone} from 'src/core/utils/object';
import SortableTree from 'react-sortable-tree';
import {UserProfileProps, withUserProfile} from "../../../../components/user-profile";
import {isReadOnly} from 'src/utils/CheckValidationbyUsers';
import { BulkInsertUpload } from '../../../../components/bulk-upload/bulkInsert-upload';
import { successNotification } from '../../../../components/systemNotifications/notificationService';

// tslint:disable-next-line:no-empty-interface
interface EmployeesViewProps extends RouteComponentProps<{
    employeeId: string,
    companyId: string,
    defaultRestricionPlanId: string
}>, WithTranslation, UserProfileProps, IdentityProps, CacheProps {
    companyName: string;
    currency: string;
    allowCir: boolean;
    allowCii: boolean;
    refresh: boolean;
    onRefresh?: () => void;
    onDelete?: () => void;
    handleResetTableSuccess?: (value: boolean) => void,
    isRefreshTable?: boolean
}

interface EmployeesViewState {
    query: Query;
    newEmployeeShown: boolean;
    isHardDeleteEmployeeShown: boolean;
    employeeToDelete?: EmployeeSummary;
    showDeleted: boolean;
    showLinkEmployee: boolean;
    employeeToLink?: EmployeeSummary;
    linkEmployeeConfirmed: boolean;
    isBusy: boolean;
    isTourOpen: boolean;
    refresh: boolean;
    openHierarchyModal: boolean;
    treeData: any,
    isEmptyListAndManager: boolean | null;
    selectedItems: any[];
}

@connect(['employees', EmployeesStore])
class EmployeesDashboard extends React.Component<EmployeesViewProps, EmployeesViewState> {
    private get employeesStore() {
        return (this.props as any).employees as EmployeesStore;
    }

    constructor(props: EmployeesViewProps) {
        super(props);

        let queryStored = props.cache.getWithCustomKey("Employees", true)

        let query = {
            searchQuery: '', orderBy: [{field: 'title', direction: 'Ascending', useProfile: true}], skip: 0, take: 10
        }

        if (queryStored)
            query = queryStored

        this.state = {
            query: query as any,
            newEmployeeShown: false,
            showDeleted: false,
            isBusy: false,
            isTourOpen: false,
            refresh: true,
            isHardDeleteEmployeeShown: false,
            linkEmployeeConfirmed: false,
            showLinkEmployee: false,
            openHierarchyModal: false,
            treeData: undefined,
            isEmptyListAndManager: null,
            selectedItems: [],
        };
    }

    componentWillMount() {
        this.employeesStore.changeUrl(this.props.match.params.companyId);
    }

    componentDidUpdate() {
        if (this.state.refresh || this.props.refresh) {
            this.state.isEmptyListAndManager ?  this.load(this.state.query) : this.checkEmployeeExist()
            this.setState({refresh: false})
            this.props.onRefresh && this.props.onRefresh()
        }
    }

    @autobind
    async loadHierarchy(showDeleted: boolean) {
        this.setState({isBusy: true})
        let hierarchy = await this.employeesStore.employeesHierarchy(showDeleted)
        this.setState({isBusy: false, treeData: hierarchy})
    }

    @autobind
    private async load(query: Query) {
        this.props.cache.saveWithCustomKey("Employees", query);
        let hierarchy = await this.employeesStore.employeesHierarchy(this.state.showDeleted)
        this.setState({treeData: hierarchy})
        if (this.state.showDeleted)
            return this.employeesStore.getAllDeletedAsync(query);
        return this.employeesStore.getAllAsync(query);
    }
    @autobind
    private async checkEmployeeExist() {
        const employeedExist = await this.employeesStore.checkEmployeeExists()
        await this.load(this.state.query)
        this.setState({isEmptyListAndManager: employeedExist})
    }

    @autobind
    private onQueryChanged(query: Query) {
        this.setState({query}, () => this.load(query));
    }

    @autobind
    private async onSaveItem(item: EmployeeSummary, state: ItemState) {
        let result = await this.employeesStore.saveAsync(`${item.id}`, item, state);
        await this.load(this.state.query);
        return result;
    }

    @autobind
    private async onDeleteRow(item: EmployeeSummary, state: ItemState): Promise<CommandResult<any>> {
        this.setState({ selectedItems: [] });
        let del = await this.employeesStore.deleteAsync(`${item.id}`);
        this.props.onDelete && this.props.onDelete()
        return del
    }

    @autobind
    private async onDelete(id: number): Promise<CommandResult<any>> {
        let del = await this.employeesStore.deleteAsync(`${id}`);
        this.props.onDelete && this.props.onDelete()
        this.setState({showLinkEmployee: false})
        return del
    }

    @autobind
    private onNewItem() {
        this.setState({newEmployeeShown: true});
    }

    @autobind
    private onNewItemClosed(id: string | undefined) {
        this.setState({newEmployeeShown: false, showLinkEmployee: false});
        if (id) {
            this.load(this.state.query);
        }
    }

    @autobind
    private showMessage(result: any) {
        if (result.success) {
            const {t} = this.props as any;
            Modal.info({
                title: t("Information"),
                content: t("The generation of the weekly sheet of the registered user will not be effective until the next day and until then it will not appear among the sheets of the subordinate team"),
                onOk() {
                }
            })
        }
    }

    @autobind
    private onChangedDeleted(checked: boolean) {
        let query = clone<Query>(this.state.query);
        query.skip = 0
        this.setState({showDeleted: checked}, () => this.load(query));
    }

    private async onRestoreEmployee(data: EmployeeSummary) {
        if (data) {
            this.setState({isBusy: true})
            await this.employeesStore.restoreEmployee(data.id.toString());
            this.setState({isBusy: false})
            this.props.onDelete && this.props.onDelete()
            await this.load(this.state.query);

        }
    }

    private onHardDeleteEmployee(data: EmployeeSummary) {
        const {t} = this.props
        if (data) {
            this.setState({isBusy: true, isHardDeleteEmployeeShown: false})
            this.employeesStore.hardDeleteEmployee(`${data.id}`).then(result => {
                notify({
                    type: "success",
                    autoClose: true,
                    description: t("Employee permanently deleted"),
                    key: "success",
                    message: t("Success"),
                })
                this.props.onDelete && this.props.onDelete()
                this.load(this.state.query);
                this.setState({isBusy: false})
            }).catch(error => {
                notify({
                    type: "error",
                    autoClose: true,
                    description: t("There was an error"),
                    key: "error",
                    message: t("Error"),
                })
                this.setState({isBusy: false})
            });

        }
    }

    private async onInviteEmployee(data: EmployeeSummary) {
        const {t} = this.props
        if (data) {
            this.employeesStore.state.isBusy = true
            this.setState({isBusy: true})
            this.employeesStore.inviteEmployee(data.id.toString()).then((result) => {
                notify({
                    type: "success",
                    autoClose: true,
                    description: t("Invite sent successfully"),
                    key: "success",
                    message: t("Success"),
                })
            }).catch(reason => {
                notify({
                    type: "error",
                    autoClose: true,
                    description: t("There was an error"),
                    key: "error",
                    message: t("Error"),
                })
            }).finally(() => {
                this.employeesStore.state.isBusy = false
                this.setState({isBusy: false})
            })
        }
    }

    @autobind
    private setIsTourOpen(state: boolean) {
        this.setState({isTourOpen: state});
    }

    private downloadExcelEmployees = async () => {
        const { query, selectedItems } = this.state;      
        await this.employeesStore.exportEmployees(query, selectedItems);
        successNotification(this.props.t('Operation Completed Successfully'), this.props.t('Your operation will be processed in background.'));
    }

    private onSelectChange = (selectedRowKeys: any[]) => {
        this.setState({ selectedItems: selectedRowKeys });
    }

    public render() {
        const {t} = this.props as any;
        let show = this.state.showDeleted;
        if (this.props.isRefreshTable) {
            this.load(this.state.query);
            if (this.props.handleResetTableSuccess) {
                this.props.handleResetTableSuccess(false);
            }
        }

        const tableModel = {
            query: this.state.query,
            columns: [
                {
                    sortable: true,
                    field: 'title',
                    title: t('Identifier'),
                    renderer: (data: EmployeeSummary) => <span>
                        <Link data-tut="reactour__navegateEmployee2" to={{
                            pathname: `/time-tracking/admin/companies/${this.props.match.params.companyId}/employees/${data.id}`,
                            state: {
                                titles: [...(this.props.location.state ? (this.props.location.state as any).titles || [] : []),
                                {
                                    url: `/time-tracking/admin/companies/${this.props.match.params.companyId}`,
                                    title: this.props.companyName
                                }]
                            }
                        }}><Icon type="user"/>&nbsp;<span>{data.title.split('(')[0]}</span><span
                            style={{fontSize: 12}}>({data.title.split('(')[1]}</span></Link>
                    </span>,
                    editor: (data: EmployeeSummary) => <Input/>
                },
                {
                    sortable: true,
                    field: 'managerTitle',
                    title: t('Line manager'),
                    renderer: (data: EmployeeSummary) => <span><span>{data.managerTitle.split('(')[0]}</span><span
                        style={{fontSize: 12}}>({data.managerTitle.split('(')[1]}</span></span>,
                    editor: (data: EmployeeSummary) => !this.props.userProfile.roles ||
                        (!this.props.userProfile.roles.includes('Manager')
                            && !this.props.userProfile.roles.includes('Administrator')
                            && !this.props.userProfile.roles.includes('ClientAdmin')
                            && !this.props.userProfile.roles.includes('FIConsultant')) ?
                        <span>{this.props.identity.email}</span> :
                        <EmployeeEditor placeholder={t("Name")} companyId={this.props.match.params.companyId} nullable/>
                },
                {
                    sortable: true,
                    field: 'function',
                    title: t('Function'),
                    renderer: (data: EmployeeSummary) => <span>{data.function}</span>,
                    editor: (data: EmployeeSummary) => <Input />
                },
                {
                    field: 'restrictionPlanTitle',
                    align: 'center',
                    title: <EditableTooltip
                        name="employee-list-working-regimes"> {t('Restriction Plan')}</EditableTooltip>,
                    renderer: (data: EmployeeSummary) => <span>{data.restrictionPlanTitle}</span>
                },
                {
                    sortable: true,
                    field: 'weekMinHours',
                    align: 'center',
                    title: t('Min Weekly Working Hours'),
                    renderer: (data: EmployeeSummary) => <span>{data.weekMinHours}</span>,
                    editor: (data: EmployeeSummary) => <InputNumber disabled/>
                },
                {
                    sortable: true,
                    field: 'weekMaxHours',
                    align: 'center',
                    title: t('Max Weekly Working Hours'),
                    renderer: (data: EmployeeSummary) => <span>{data.weekMaxHours}</span>,
                    editor: (data: EmployeeSummary) => <InputNumber disabled />
                },
                {
                    field: 'cloneTimesheet',
                    align: 'center',
                    title: <EditableTooltip name='employee-clone-timesheet'> {t('Clone timesheet')} </EditableTooltip>,
                    renderer: (data: EmployeeSummary) => <span>{data.cloneTimesheet ? t("Yes") : t("No")}</span>,
                    editor: (data: EmployeeSummary) => <Checkbox/>
                },
                {
                    field: "idss",
                    title: "",
                    renderer: (data: EmployeeSummary) => (
                        <EditableTooltip name="employee-list-restore-btn" noIcon>
                            <Button
                                disabled={isReadOnly(this.props.userProfile)}
                                onClick={async () => await this.onRestoreEmployee(data)}>
                                {t('Restore')}
                                <Icon type="info-circle"
                                      style={{fontSize: '12px', color: 'grey', marginRight: 3, marginLeft: 3}}/>
                            </Button>
                        </EditableTooltip>),
                    editor: (data: EmployeeSummary) => <span></span>,
                    visible: this.state.showDeleted,
                    align: 'center'
                },
                {
                    field: "harddelete",
                    title: "",
                    renderer: (data: EmployeeSummary) => (
                        <Button
                            disabled={isReadOnly(this.props.userProfile)}
                            type="danger"
                            onClick={() => this.setState({isHardDeleteEmployeeShown: true, employeeToDelete: data})}
                            style={(isReadOnly) ? {} : {color: "red", backgroundColor: "white"}}>
                            {t('Delete')}
                            <Icon type="delete" style={(isReadOnly) ? {
                                fontSize: '12px',
                                marginRight: 3,
                                marginLeft: 3
                            } : {fontSize: '12px', color: 'red', marginRight: 3, marginLeft: 3}}/>
                        </Button>),
                    editor: (data: EmployeeSummary) => <span></span>,
                    visible: this.state.showDeleted,
                    align: 'center'
                },
                {
                    field: "iid",
                    title: "",
                    renderer: (data: EmployeeSummary) => {
                        if (!data.isUnlinked) {
                            return <Popconfirm
                                disabled={isReadOnly(this.props.userProfile)}
                                title={t("Are you sure you want to send the invitation?")}
                                onConfirm={async () => await this.onInviteEmployee(data)}
                                okText={t("Yes")}
                                cancelText={t("No")}
                                placement="bottomRight">
                                <Button disabled={isReadOnly(this.props.userProfile)} hidden={this.state.showDeleted}
                                        style={{paddingLeft: '4%', paddingRight: '4%'}}>
                                    {t('Invite')}
                                </Button>
                            </Popconfirm>
                        } else if (!this.state.showDeleted) {
                            return <EditableTooltip name="sync-employee" noIcon>
                                <Button
                                    disabled={isReadOnly(this.props.userProfile)}
                                    onClick={() => {
                                        this.setState({showLinkEmployee: true, employeeToLink: data});
                                    }}
                                    style={{paddingLeft: '4%', paddingRight: '4%'}}
                                >
                                    {t('Synchronize')} <Icon type="info-circle" style={{
                                        fontSize: '12px',
                                        color: 'grey',
                                        marginRight: 3,
                                        marginLeft: 3
                                }}/>
                                </Button>
                            </EditableTooltip>
                        }
                    },
                    editor: (data: EmployeeSummary) => <span></span>,
                    visible: !this.state.showDeleted
                }

            ],
            data: this.employeesStore.state,
            sortFields: [{
                field: 'title',
                text: t('Identifier'),
                useProfile: true
            },
            {
                field: 'managerTitle',
                text: t('Hierarchical manager'),
                useProfile: true
            },
            {
                field: 'function',
                text: t('Function'),
                useProfile: true
            },
            {
                field: 'weekMinHours',
                text: t('Min Weekly Working Hours'),
                useProfile: true
            },

            {
                field: 'weekMaxHours',
                text: t('Max Weekly Working Hours'),
                useProfile: true
            },],
        } as TableModel<EmployeeSummary>;

        const hasSelected = tableModel.data.items.some((x) => this.state.selectedItems.some(t => t == (x.item as any)['id']));

        const NodeTitle = ({ node }: { node: any }) => {

            return (
                <div className="node-title-container">
                    {!node.title || node.title.length == 0 ? t('(Empty Name)') : node.title}
                </div>
            );
        };

        return <div>
            <Row gutter={0}>
                <Col span={2}>
                    <div data-tut="reactour__navegateEmployee1" style={{
                        width: '100%',
                        height: '30px',
                        zIndex: -1,
                        position: 'relative',
                        bottom: '-105px'
                    }}></div>
                </Col>
                <Col span={10}>
                    <Tour
                        steps={(isReadOnly(this.props.userProfile)) ? steps(t).stepsEmployeesReadOnly : steps(t).stepsEmployees}
                        isOpen={this.state.isTourOpen}
                        onRequestClose={() => this.setIsTourOpen(false)}
                    />
                </Col>
                <Col span={12}>
                    <Button data-tut="reactour__employeeInit" type={"link"} style={{float: 'right'}}
                        onClick={() => this.setIsTourOpen(true)}
                        icon={'caret-right-fill'}>  {t("Discover Employees")} </Button>
                </Col>
            </Row>
            <Row>
                <span>
                    <EditableTooltip name="employee-list-show-deleted"><span>{t('Show Eliminated Employees?') + ' '}</span></EditableTooltip>
                    <Switch title={t('Show Eliminated Employees?')} onChange={this.onChangedDeleted}></Switch>
                </span>
                <span style={{marginLeft: '15px'}}>
                    <Button icon="apartment"
                            onClick={() => this.setState({openHierarchyModal: true})}>{t("Employee hierarchy")}</Button>
                </span>
            </Row>
            <Divider/>

            <Row type={'flex'} align='top' justify='space-between'>
                <Card style={{width: '100%', marginTop: -20}}>
                    <TableView
                        showDeleteStatusWindow
                        onSelectedChange={this.onSelectChange}
                        rowKey='id'
                        model={tableModel}
                        error={this.employeesStore.state.result && !this.employeesStore.state.result.isSuccess && formatMessage(this.employeesStore.state.result)}
                        embedded={false}
                        onQueryChanged={(q: Query) => this.onQueryChanged(q)}
                        onRefresh={() => this.load(this.state.query)}
                        canEdit={false}
                        canDelete={!this.state.showDeleted && !isReadOnly(this.props.userProfile)}
                        onDeleteRow={this.onDeleteRow}
                        canCreateNew={!this.state.showDeleted && !isReadOnly(this.props.userProfile)}
                        bulkTemplateName={t('employees.xlsx')}
                        onMessageUploadClosed={this.showMessage}
                        onNewItem={this.onNewItem}
                        scroll={{ x: 1 }}
                        leftToolbar={
                            <>
                                <li>
                                    <BulkInsertUpload
                                        bulkTemplateName={t('employees.xlsx')}
                                        bulkInsertUrl={!this.state.showDeleted ? (isReadOnly(this.props.userProfile)) ? undefined : `api/v1/companies/${this.props.match.params.companyId}/employees/bulk_insert` : undefined}
                                        bulkTemplateUrl={!this.state.showDeleted ? (isReadOnly(this.props.userProfile)) ? undefined : `api/v1/companies/${this.props.match.params.companyId}/employees/template` : undefined}
                                    />
                                </li>
                                <li>
                                    <Tooltip placement="topLeft" title={t(`${hasSelected && this.state.selectedItems.length > 0 ? 'Download selected' : 'Download All'}`)}>
                                        <Button onClick={() => this.downloadExcelEmployees()}
                                            style={{ backgroundColor: 'rgb(0, 174, 255)', color: 'white' }}
                                            disabled={this.employeesStore.state.items.length == 0}
                                        >
                                            <Icon type='download' />
                                        </Button>
                                    </Tooltip>
                                </li>
                            </>
                        }>

                    </TableView>
                </Card>
            </Row>
            <Modal
                visible={this.state.isHardDeleteEmployeeShown}
                footer={[
                    <Button onClick={() => {
                        this.setState({isHardDeleteEmployeeShown: false})
                    }} key="1">{t("Cancel")}</Button>,
                    <Button type='danger' onClick={() => {
                        if (this.state.employeeToDelete) this.onHardDeleteEmployee(this.state.employeeToDelete)
                    }} key="2">
                        <div style={{color: 'white'}}>{t("Delete")}</div>
                    </Button>,
                ]}
                onCancel={() => {
                    this.setState({isHardDeleteEmployeeShown: false})
                }}
                title={t("Delete employee permanently")}
                width={800}>
                {t("This action cannot be reversed. Every timesheet related to the employee will be deleted permanently, and so is the constance of the employee being a member of a project. Are you sure you want to continue?")}
            </Modal>
            <Modal
                width={'100%'}
                visible={this.state.openHierarchyModal}
                closable={true}
                style={{padding: 20}}
                onCancel={() => this.setState({openHierarchyModal: false})}
                title={t('Employee hierarchy')}
                footer={[]}
            >
                <div>
                    <Row>
                        <span>{t('Show Eliminated Employees?') + ' '}</span> <Switch
                            title={t('Show Eliminated Employees?')} onChange={(val) => this.loadHierarchy(val)}></Switch>
                    </Row>

                    {this.state.treeData && <SortableTree
                        canDrag={false}
                        isVirtualized={false}
                        getNodeKey={({node}: any) => node.id}
                        treeData={this.state.treeData}
                        onChange={treeData => this.setState({treeData})}
                        generateNodeProps={({node}: any) => ({
                            className: undefined,
                            title: <NodeTitle node={node}/>,
                        })}
                    />
                    }
                </div>
            </Modal>

            <EmployeeLinkerView
                visible={this.state.showLinkEmployee}
                companyId={this.props.match.params.companyId}
                employee={this.state.employeeToLink}
                onClose={this.onNewItemClosed}
                onDelete={this.onDelete}/>
            {this.state.newEmployeeShown && <NewEmployeeView
                userProfile={this.props.userProfile}
                companyId={this.props.match.params.companyId}
                onClose={this.onNewItemClosed}
                allowCir={this.props.allowCir}
                allowCii={this.props.allowCii}
                currency={this.props.currency}
            />}
        </div>
    }
}

// Wire up the React component to the Redux store
export default withUserProfile(withCache(withTranslation()(withIdentity(withRouter(EmployeesDashboard)))));
